import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, ArrowLeft, Play, Trash2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface WorkflowStage {
  name: string;
  status: 'pending' | 'running' | 'success' | 'error';
  message?: string;
  duration?: number;
}

const WorkflowTest = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [stages, setStages] = useState<WorkflowStage[]>([
    { name: 'Setup', status: 'pending' },
    { name: 'Data Collection', status: 'pending' },
    { name: 'AI Analysis', status: 'pending' },
    { name: 'Article Generation', status: 'pending' },
    { name: 'Social Media Posting', status: 'pending' },
    { name: 'Verification', status: 'pending' },
  ]);
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  const updateStage = (index: number, updates: Partial<WorkflowStage>) => {
    setStages(prev => prev.map((stage, i) => 
      i === index ? { ...stage, ...updates } : stage
    ));
  };

  const clearData = async () => {
    if (!confirm('This will delete all blog posts and crypto data from today. Continue?')) {
      return;
    }

    try {
      toast({
        title: "Clearing data...",
        description: "Deleting today's articles and cached data",
      });

      // Delete today's blog posts
      const { error: postsError } = await supabase
        .from('blog_posts')
        .delete()
        .gte('created_at', new Date().toISOString().split('T')[0]);

      if (postsError) throw postsError;

      // Delete today's crypto data
      const { error: dataError } = await supabase
        .from('crypto_market_data')
        .delete()
        .gte('collected_at', new Date().toISOString().split('T')[0]);

      if (dataError) throw dataError;

      toast({
        title: "Data Cleared",
        description: "Ready for fresh test run",
      });
    } catch (error: any) {
      toast({
        title: "Clear Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const runFullWorkflow = async () => {
    if (!isAdmin) {
      toast({
        title: "Admin Required",
        description: "You must be an admin to run this test",
        variant: "destructive",
      });
      return;
    }

    setIsRunning(true);
    const startTime = Date.now();

    try {
      // Stage 0: Setup
      updateStage(0, { status: 'running', message: 'Initializing test...' });
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStage(0, { 
        status: 'success', 
        message: 'Test environment ready',
        duration: Date.now() - startTime 
      });

      // Stage 1: Data Collection
      const dataStartTime = Date.now();
      updateStage(1, { status: 'running', message: 'Fetching crypto market data...' });
      
      const { error: dataError } = await supabase.functions.invoke('collect-crypto-data', {
        body: {},
      });

      if (dataError) {
        updateStage(1, { 
          status: 'error', 
          message: dataError.message 
        });
        throw dataError;
      }

      updateStage(1, { 
        status: 'success', 
        message: 'Market data collected successfully',
        duration: Date.now() - dataStartTime
      });

      // Stage 2: AI Analysis
      const analysisStartTime = Date.now();
      updateStage(2, { status: 'running', message: 'Running AI market analysis...' });
      
      const { error: analysisError } = await supabase.functions.invoke('ai-analyze-market', {
        body: {},
      });

      if (analysisError) {
        updateStage(2, { 
          status: 'error', 
          message: analysisError.message 
        });
        throw analysisError;
      }

      updateStage(2, { 
        status: 'success', 
        message: 'AI analysis completed',
        duration: Date.now() - analysisStartTime
      });

      // Stage 3: Article Generation
      const articleStartTime = Date.now();
      updateStage(3, { status: 'running', message: 'Generating blog articles...' });
      
      const { data: articleData, error: articleError } = await supabase.functions.invoke('generate-blog-post', {
        body: { postType: 'daily_digest' },
      });

      if (articleError) {
        updateStage(3, { 
          status: 'error', 
          message: articleError.message 
        });
        throw articleError;
      }

      const articlesGenerated = articleData?.results?.length || 0;
      updateStage(3, { 
        status: 'success', 
        message: `${articlesGenerated} articles generated`,
        duration: Date.now() - articleStartTime
      });

      // Stage 4: Social Media (optional, may not be configured)
      const socialStartTime = Date.now();
      updateStage(4, { status: 'running', message: 'Posting to social media...' });
      
      // Attempt to post to Twitter (will skip if not configured)
      try {
        const { error: twitterError } = await supabase.functions.invoke('post-to-twitter', {
          body: { postType: 'best_coin' },
        });

        if (twitterError) {
          updateStage(4, { 
            status: 'error', 
            message: `Twitter posting failed: ${twitterError.message}`,
            duration: Date.now() - socialStartTime
          });
        } else {
          updateStage(4, { 
            status: 'success', 
            message: 'Posted to Twitter successfully',
            duration: Date.now() - socialStartTime
          });
        }
      } catch (socialError: any) {
        updateStage(4, { 
          status: 'success', 
          message: 'Social media not configured (skipped)',
          duration: Date.now() - socialStartTime
        });
      }

      // Stage 5: Verification
      const verifyStartTime = Date.now();
      updateStage(5, { status: 'running', message: 'Verifying results...' });
      
      // Verify blog posts were created
      const { data: posts, error: verifyError } = await supabase
        .from('blog_posts')
        .select('id, title, slug')
        .gte('created_at', new Date().toISOString().split('T')[0])
        .order('created_at', { ascending: false })
        .limit(5);

      if (verifyError) throw verifyError;

      updateStage(5, { 
        status: 'success', 
        message: `Verified ${posts?.length || 0} articles in database`,
        duration: Date.now() - verifyStartTime
      });

      const totalTime = Date.now() - startTime;
      toast({
        title: "Workflow Complete! 🎉",
        description: `Total time: ${(totalTime / 1000).toFixed(1)}s`,
      });

    } catch (error: any) {
      console.error('Workflow error:', error);
      toast({
        title: "Workflow Failed",
        description: error.message || "Check console for details",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  const getStageIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-5 h-5 animate-spin text-blue-600" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-muted" />;
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>
              This testing interface is only available to administrators
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            🔄 Full Workflow Test
          </h1>
          <p className="text-muted-foreground">
            Test the complete automation pipeline from data collection to article publication
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Test Controls</CardTitle>
            <CardDescription>
              Run the complete workflow or clear data for a fresh test
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <Button
              onClick={runFullWorkflow}
              disabled={isRunning}
              size="lg"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Full Workflow
                </>
              )}
            </Button>

            <Button
              onClick={clearData}
              disabled={isRunning}
              variant="outline"
              size="lg"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Today's Data
            </Button>

            <Button
              onClick={() => window.location.reload()}
              disabled={isRunning}
              variant="ghost"
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset Test
            </Button>
          </CardContent>
        </Card>

        {/* Workflow Stages */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow Progress</CardTitle>
            <CardDescription>
              Monitor each stage of the automation pipeline
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {stages.map((stage, index) => (
              <div
                key={index}
                className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all ${
                  stage.status === 'running' ? 'border-blue-500 bg-blue-500/5' :
                  stage.status === 'success' ? 'border-green-500 bg-green-500/5' :
                  stage.status === 'error' ? 'border-red-500 bg-red-500/5' :
                  'border-muted'
                }`}
              >
                <div className="flex-shrink-0 mt-1">
                  {getStageIcon(stage.status)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold">{stage.name}</h4>
                    {stage.status !== 'pending' && (
                      <Badge variant={
                        stage.status === 'success' ? 'default' :
                        stage.status === 'error' ? 'destructive' :
                        'secondary'
                      }>
                        {stage.status}
                      </Badge>
                    )}
                  </div>
                  {stage.message && (
                    <p className="text-sm text-muted-foreground">{stage.message}</p>
                  )}
                  {stage.duration !== undefined && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Duration: {(stage.duration / 1000).toFixed(1)}s
                    </p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Test Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">What This Test Does:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>1. Clears cache and prepares test environment</li>
                <li>2. Fetches crypto data from CoinGecko</li>
                <li>3. Runs AI analysis on collected data</li>
                <li>4. Generates 2 blog articles with affiliate links</li>
                <li>5. Attempts to post to social media (if configured)</li>
                <li>6. Verifies articles were saved to database</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">After Test Completes:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ Navigate to homepage to see articles</li>
                <li>✓ Click on an article to verify content renders</li>
                <li>✓ Check affiliate links work</li>
                <li>✓ Test mobile responsiveness</li>
                <li>✓ Check page load speed</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkflowTest;
